import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must a German invoice (Rechnung) include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A German invoice (Rechnung) must include: your full name and address; your tax number (Steuernummer) or VAT ID (Umsatzsteuer-ID); the client name and address; a unique invoice number; the invoice date and date of supply; an itemized description of goods or services; the net amount, applicable VAT rate (19% or 7%), VAT amount, and gross total. Invoices below EUR 250 (Kleinbetragsrechnung) have simplified requirements.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the VAT (Mehrwertsteuer) rate in Germany?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Germany has two VAT (Mehrwertsteuer/USt) rates. The standard rate is 19%, which applies to most goods and services. The reduced rate of 7% applies to food, books, newspapers, and certain cultural services. German VAT registration is compulsory for businesses exceeding EUR 22,000 annual turnover (Kleinunternehmerregelung threshold).',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the Kleinunternehmerregelung in Germany?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The Kleinunternehmerregelung (small business regulation) allows businesses with annual turnover below EUR 22,000 (and expected turnover below EUR 50,000 in the following year) to opt out of VAT registration. Small businesses do not charge VAT but must write "Kein Ausweis von Umsatzsteuer, da Kleinunternehmer gemäß § 19 UStG" on their invoices.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do German freelancers (Freiberufler) invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'German freelancers (Freiberufler) billing EU business clients can apply reverse charge — the client accounts for VAT in their own country, and you write "Reverse Charge" on the invoice. For non-EU clients, services are generally zero-rated. Use the international invoice type to bill in EUR, USD, or any currency and include your IBAN for EU transfers.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for German users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for German freelancers and businesses. Create Rechnungen (invoices) with 19% VAT auto-calculated, or standard invoices under the Kleinunternehmerregelung. Download a professional PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Percent,
    title: 'VAT Invoice (Rechnung)',
    subtitle: 'For VAT-registered German businesses',
    features: ['19% VAT auto-calculated', 'Steuernummer / USt-ID field', 'Net + VAT + gross total', 'Finanzamt-compliant format', 'EUR currency'],
    to: '/create/vat',
    color: 'amber',
    btnClass: 'bg-amber-600 hover:bg-amber-700',
  },
  {
    icon: FileText,
    title: 'Kleinunternehmer Invoice',
    subtitle: 'For businesses below EUR 22,000',
    features: ['No VAT charged', 'EUR currency', 'Custom line items', 'Logo upload', 'Steuernummer field'],
    to: '/create/freelancer',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients',
    features: ['USD, GBP billing', 'Reverse charge support', 'IBAN / SWIFT fields', 'Multi-currency', 'Professional PDF'],
    to: '/create/international',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorGermanyPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Germany: Free Rechnung Maker for German Freelancers | Invilic"
        description="Free invoice generator for German businesses and freelancers. Create VAT invoices (Rechnung) with 19% VAT or Kleinunternehmer invoices in EUR. PDF download free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-germany"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇩🇪 Germany
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Germany
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free Rechnung (invoice) generator for German businesses, freelancers, and Freiberufler. Create VAT invoices with 19% Mehrwertsteuer auto-calculated, or Kleinunternehmer invoices without VAT. Download professional PDF invoices instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/vat" className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-amber-600/20 transition-all duration-200">
              Create VAT Rechnung <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Kleinunternehmer Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">VAT Rechnung vs Kleinunternehmer Invoice in Germany</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-amber-700 dark:text-amber-400 mb-3">Use a VAT Rechnung if:</div>
              {[
                'Your annual turnover exceeds EUR 22,000 and you are VAT-registered',
                'Your client is a VAT-registered business that needs to reclaim input VAT',
                'You bill EU business clients (reverse charge may apply)',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a Kleinunternehmer Invoice if:</div>
              {[
                'Your annual turnover is below EUR 22,000 (Kleinunternehmer status)',
                'You are a Freiberufler or freelancer below the VAT threshold',
                'You do not charge VAT and note § 19 UStG on your invoice',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Germany Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your German Rechnung for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF Rechnung for German businesses in under 2 minutes. No account required.</p>
          <Link to="/create/vat" className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-amber-600/20 transition-all duration-200 text-lg">
            Create Rechnung Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
